import React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Users, Luggage, Check, Car } from "lucide-react";
import { useI18n } from "../i18n";
import { useBooking } from "../lib/booking-context";

export const Fleet = ({ config }) => {
  const { t } = useI18n();
  const { requestBooking } = useBooking();
  const car = (config?.fleet || [])[0];

  if (!car) return null;

  return (
    <section id="fleet" className="relative py-14 sm:py-18 lg:py-24 overflow-hidden" data-testid="fleet-section">
      <div className="mesh-glow top-10 left-[-10rem]" />
      <div className="relative max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="eyebrow">{t("fleet.title")}</span>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.01em]">{t("fleet.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("fleet.subtitle")}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 rounded-[22px] border border-border bg-card flat-card overflow-hidden grid grid-cols-1 lg:grid-cols-2"
          data-testid={`fleet-card-${car.id}`}
        >
          <div className="relative min-h-[260px] lg:min-h-[380px]">
            <img src={car.image} alt={car.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground rounded-full">{car.class}</Badge>
          </div>

          <div className="p-6 sm:p-9 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-[hsl(var(--gold-deep))]">
              <Car className="h-5 w-5" />
              <span className="eyebrow">Premium sedan-avant</span>
            </div>
            <h3 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">{car.name}</h3>
            <p className="mt-2 text-muted-foreground">{car.class}</p>

            <div className="mt-5 flex items-center gap-6 text-sm">
              <span className="flex items-center gap-2"><span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15"><Users className="h-4 w-4 text-[hsl(var(--gold-deep))]" /></span> {car.passengers} {t("fleet.pax")}</span>
              <span className="flex items-center gap-2"><span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15"><Luggage className="h-4 w-4 text-[hsl(var(--gold-deep))]" /></span> {car.luggage} {t("fleet.lug")}</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {car.features.map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5 text-sm rounded-full bg-secondary px-3 py-1.5">
                  <Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> {t(`fleet.features.${f}`)}
                </span>
              ))}
            </div>

            <Button className="mt-7 rounded-full shimmer active:scale-[0.98] w-full sm:w-auto h-12 px-7" onClick={() => requestBooking({ comment: car.name })} data-testid={`fleet-select-${car.id}`}>
              {t("fleet.select")}
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
